import React from 'react';
import { toEnglishDigits, toMarathiDigits } from '../../utils/marathi';
import './FormStyles.css';

export default function NumberInput({
  label,
  name,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  min = 0,
  max = null,
  step = 1,
  placeholder = '',
  className = '',
}) {
  const displayedValue = toMarathiDigits(value ?? '');
  const displayedPlaceholder = toMarathiDigits(placeholder);

  const normalizeNumberValue = rawValue => {
    const english = toEnglishDigits(rawValue).replace(/[^\d.]/g, '');
    const [whole, ...decimalParts] = english.split('.');
    return decimalParts.length ? `${whole}.${decimalParts.join('')}` : whole;
  };

  const handleInputChange = event => {
    const nextValue = normalizeNumberValue(event.target.value);
    onChange?.({
      ...event,
      target: {
        ...event.target,
        name,
        value: nextValue,
        type: 'number',
      },
      currentTarget: {
        ...event.currentTarget,
        name,
        value: nextValue,
        type: 'number',
      },
    });
  };

  const preventInvalidNumberInput = (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    if (['+', '-', 'e', 'E'].includes(event.key)) {
      event.preventDefault();
      return;
    }
    if (event.key === '.' && String(value || '').includes('.')) {
      event.preventDefault();
      return;
    }
    if (event.key.length === 1 && !/[\d०-९.]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handlePaste = (event) => {
    const text = event.clipboardData?.getData('text') || '';
    if (/[+\-eE]/.test(text)) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    const nextValue = normalizeNumberValue(text);
    onChange?.({
      ...event,
      target: { name, value: nextValue, type: 'number' },
      currentTarget: { name, value: nextValue, type: 'number' },
    });
  };

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      <input
        id={name}
        type="text"
        name={name}
        value={displayedValue}
        onChange={handleInputChange}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        inputMode={String(step).includes('.') ? 'decimal' : 'numeric'}
        placeholder={displayedPlaceholder}
        data-marathi-input="false"
        onKeyDown={preventInvalidNumberInput}
        onPaste={handlePaste}
        className={`form-input ${error ? 'error' : ''}`}
      />
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}
